import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import Select from "react-select";
import { logout } from "../../app/auth/authSlice.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import RiderWalletDrawer from "./riderWalletDrawer.jsx";
import "../compnay/ManageCompany.css";

const APPROVAL_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Suspended", label: "Suspended" },
];

const ONLINE_OPTIONS = [
  { value: "", label: "All online states" },
  { value: "true", label: "Online" },
  { value: "false", label: "Offline" },
];

const formatMoney = (value) =>
  Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const ManageMarketplaceRiders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const [search, setSearch] = useState("");
  const [approvalStatus, setApprovalStatus] = useState(APPROVAL_OPTIONS[0]);
  const [onlineFilter, setOnlineFilter] = useState(ONLINE_OPTIONS[0]);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [walletRider, setWalletRider] = useState(null);

  const fetchRiders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        Search: search.trim() || "",
        SortBy: "CreatedAt",
        SortOrder: "DESC",
        PageSize: pageSize.value,
        PageNumber: pageNumber,
      };
      if (approvalStatus?.value) params.ApprovalStatus = approvalStatus.value;
      if (onlineFilter?.value !== "") params.IsOnline = onlineFilter.value === "true";

      const response = await axiosInstance.get(APIs.GET_ALL_MARKETPLACE_RIDERS, { params });
      if (response.data.status) {
        setRiders(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || response.data.Message || "Failed to fetch riders");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 1000);
      } else {
        showErrorToast("Failed to fetch riders");
      }
    } finally {
      setLoading(false);
    }
  }, [search, approvalStatus?.value, onlineFilter?.value, pageSize.value, pageNumber, dispatch, navigate]);

  useEffect(() => {
    const handler = setTimeout(() => fetchRiders(), 300);
    return () => clearTimeout(handler);
  }, [fetchRiders]);

  useEffect(() => {
    setPageNumber(1);
  }, [search, approvalStatus, onlineFilter, pageSize.value]);

  const handleApproval = async (riderId, status) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_SET_MARKETPLACE_RIDER_APPROVAL, {
        marketplaceRider_ID: riderId,
        approvalStatus: status,
      });
      if (response.data.status) {
        showSuccessToast(`Rider ${status.toLowerCase()}`);
        fetchRiders();
      } else {
        showErrorToast(response.data.message || "Action failed");
      }
    } catch {
      showErrorToast("Action failed");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setApprovalStatus(APPROVAL_OPTIONS[0]);
    setOnlineFilter(ONLINE_OPTIONS[0]);
  };

  const statusBadge = (status) => {
    const map = {
      Pending: "bg-warning text-dark",
      Approved: "bg-success",
      Rejected: "bg-danger",
      Suspended: "bg-secondary",
    };
    return <span className={`badge ${map[status] || "bg-secondary"}`}>{status}</span>;
  };

  const filterInputStyle = {
    backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.9)" : "rgba(255, 255, 255, 0.9)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(255, 255, 255, 0.3)",
    color: isDarkMode ? "#ffffff" : "#333",
    borderRadius: "6px",
    height: "36px",
  };

  const actionBtnStyle = (color, borderColor = color) => ({
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    border: `1px solid ${borderColor}`,
    color,
    padding: 0,
  });

  const riderName = (item) =>
    item.name || `${item.firstName || ""} ${item.lastName || ""}`.trim() || "—";

  const residenceZone = (item) =>
    item.residenceZoneName ||
    (Array.isArray(item.zones) && item.zones[0] ? item.zones[0].zoneName : null) ||
    "—";

  const COLUMN_COUNT = 12;

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Go Fatafat Riders</li>
          </ol>
          <h1 className="page-header mb-0">Go Fatafat Riders</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={() => navigate("/marketplaceRider/addRider")}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Rider
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: `0 4px 20px ${themeColor}40`,
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-center">
                    <div className="col-xl-4 col-lg-4 col-md-6">
                      <div className="search-input filter-control">
                        <input
                          type="text"
                          className="filter-input form-control"
                          placeholder="Search name, email, phone, CNIC, vehicle..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          style={filterInputStyle}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={approvalStatus}
                          onChange={setApprovalStatus}
                          options={APPROVAL_OPTIONS}
                          styles={dropdownStyles}
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <div className="filter-control">
                        <Select
                          value={onlineFilter}
                          onChange={setOnlineFilter}
                          options={ONLINE_OPTIONS}
                          styles={dropdownStyles}
                          className="filter-dropdown"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                    <div className="col-xl-1 col-lg-1 col-md-6">
                      <div className="filter-control">
                        <PageSizeDropDownSelection
                          defaultValue={pageSize}
                          onChange={setPageSize}
                          styles={dropdownStyles}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-12">
                      <button
                        type="button"
                        className="btn"
                        onClick={clearFilters}
                        title="Clear all filters"
                        style={{
                          height: "36px",
                          backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa fa-times me-1"></i>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="table-responsive table-container"
              style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto", position: "relative", zIndex: 1 }}
            >
              <table className="table table-hover align-middle mb-0">
                <thead
                  className="sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2">Rider</th>
                    <th className="pt-2 pb-2">Contact</th>
                    <th className="pt-2 pb-2">CNIC</th>
                    <th className="pt-2 pb-2">Vehicle</th>
                    <th className="pt-2 pb-2">Employment</th>
                    <th className="pt-2 pb-2">Location</th>
                    <th className="pt-2 pb-2 text-center">Status</th>
                    <th className="pt-2 pb-2 text-center">Online</th>
                    <th className="pt-2 pb-2 text-center">Deliveries</th>
                    <th className="pt-2 pb-2 text-center">Rating</th>
                    <th className="pt-2 pb-2 text-center">Stars</th>
                    <th className="pt-2 pb-2 text-center" style={{ width: 160 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {riders.length > 0 ? (
                    riders.map((item) => {
                      const id = item.marketplaceRiderId || item.marketplaceRider_ID;
                      const isSalaried = item.employmentType === "Salaried";
                      const vehicleLabel = [item.vehicleType, item.vehicleNumber].filter(Boolean).join(" · ") || "—";
                      const cityLabel = [item.serviceCountryCode, item.serviceCity].filter(Boolean).join(" / ") || "—";

                      return (
                        <tr key={id}>
                          <td>
                            <div className="fw-semibold text-nowrap">{riderName(item)}</div>
                            <div className="small text-muted">#{id}</div>
                          </td>
                          <td>
                            <div className="text-nowrap">{item.phone || "—"}</div>
                            <div className="small text-muted text-truncate" style={{ maxWidth: 180 }}>
                              {item.email || "—"}
                            </div>
                          </td>
                          <td className="text-nowrap">{item.cnicNumber || "—"}</td>
                          <td>
                            <div className="text-nowrap">{vehicleLabel}</div>
                          </td>
                          <td>
                            <div className="text-nowrap">{item.employmentType || "—"}</div>
                            <div className="small text-muted text-nowrap">
                              {isSalaried
                                ? `${item.hoursShift ? `${item.hoursShift}h · ` : ""}Salary ${formatMoney(item.salary)}`
                                : `Comm. ${formatMoney(item.perOrderCommission)}`}
                            </div>
                          </td>
                          <td>
                            <div className="text-nowrap">{cityLabel}</div>
                            <div className="small text-muted" style={{ maxWidth: 160, whiteSpace: "normal" }}>
                              {residenceZone(item)}
                            </div>
                          </td>
                          <td className="text-center">{statusBadge(item.approvalStatus)}</td>
                          <td className="text-center">
                            <span className={`badge ${item.isOnline ? "bg-success" : "bg-secondary"}`}>
                              {item.isOnline ? "Online" : "Offline"}
                            </span>
                          </td>
                          <td className="text-center">{item.totalDeliveries ?? 0}</td>
                          <td className="text-center">{(item.rating ?? 0).toFixed(1)}</td>
                          <td className="text-center">{item.starLevel ? `${item.starLevel}/5` : "—"}</td>
                          <td className="text-center">
                            <div className="d-inline-flex justify-content-center gap-1 flex-nowrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setWalletRider({ id, name: riderName(item) })}
                                title="Wallet"
                                style={actionBtnStyle("#6c757d")}
                              >
                                <i className="fa fa-wallet" style={{ fontSize: "12px" }} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigate(`/marketplaceRider/updateRider/${id}`)}
                                title="Edit"
                                style={actionBtnStyle(themeColor)}
                              >
                                <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                              </button>
                              {item.approvalStatus === "Pending" && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleApproval(id, "Approved")}
                                    title="Approve"
                                    style={actionBtnStyle("#28a745")}
                                  >
                                    <i className="fa fa-check" style={{ fontSize: "12px" }} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleApproval(id, "Rejected")}
                                    title="Reject"
                                    style={actionBtnStyle("#dc3545")}
                                  >
                                    <i className="fa fa-times" style={{ fontSize: "12px" }} />
                                  </button>
                                </>
                              )}
                              {item.approvalStatus === "Approved" && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleApproval(id, "Suspended")}
                                  title="Suspend"
                                  style={actionBtnStyle("#ffc107")}
                                >
                                  <i className="fa fa-ban" style={{ fontSize: "12px" }} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={COLUMN_COUNT} className="text-center py-4" style={{ fontSize: "16px" }}>
                        {loading ? "Loading riders..." : "No riders found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRecords={totalRecords}
              pageSize={parseInt(pageSize.value, 10) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>

      <RiderWalletDrawer
        riderId={walletRider?.id}
        riderName={walletRider?.name}
        isOpen={!!walletRider}
        onClose={() => setWalletRider(null)}
      />
    </div>
  );
};

export default ManageMarketplaceRiders;
